import { Link } from 'react-router-dom';

export default function ServicesSection() {
  const services = [
    {
      icon: 'ri-shield-user-line',
      title: 'Security Services',
      description: 'Comprehensive security solutions including surveillance, access control, and on-site protection. Our team of experienced professionals ensures your property remains secure 24/7.',
      features: ['24/7 Monitoring', 'Access Control Systems', 'Security Assessments', 'Emergency Response'],
      link: null
    },
    {
      icon: 'ri-thunderstorms-line',
      title: 'Storm Preparedness',
      description: 'Proactive storm preparation and emergency planning to protect your property from severe weather. We help you prepare before the storm hits and recover quickly afterward.',
      features: ['Property Inspections', 'Emergency Planning', 'Storm Shutters', 'Post-Storm Recovery'],
      link: null
    },
    {
      icon: 'ri-tools-line',
      title: 'Preventative Maintenance',
      description: 'Regular maintenance programs designed to identify and address potential issues before they become costly problems. Keep your property in optimal condition year-round.',
      features: ['Routine Inspections', 'Equipment Maintenance', 'Safety Compliance', 'Repair Services'],
      link: null
    },
    {
      icon: 'ri-file-shield-line',
      title: 'Risk Assessment',
      description: 'Detailed property risk assessments leveraging our law enforcement expertise. We identify vulnerabilities and provide actionable recommendations to enhance your security posture.',
      features: ['Vulnerability Analysis', 'Threat Evaluation', 'Custom Solutions', 'Compliance Review'],
      link: null
    },
    {
      icon: 'ri-camera-line',
      title: 'Surveillance Systems',
      description: 'State-of-the-art surveillance technology installation and monitoring. High-definition cameras, remote access, and intelligent analytics to keep you informed and protected.',
      features: ['HD Camera Installation', 'Remote Monitoring', 'Video Analytics', 'Cloud Storage'],
      link: null
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Insurance Benefits',
      description: 'Professional documentation and risk management services that help lower premiums, strengthen claims, and ensure you\'re properly covered. Maximize your insurance value.',
      features: ['Premium Reduction', 'Claims Documentation', 'Pre-Loss Records', 'Liability Protection'],
      link: null
    }
  ];

  const serviceMatrix = [
    { service: 'Property & Home Inspections', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Security & Vulnerability Assessments', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Construction & Maintenance Consulting', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Preventative Maintenance Planning', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Storm Preparedness & Post Event Assessments', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Property / Building Management Oversight', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Minor Repairs Coordination (pavers, hardware, lighting)', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Structural & Site Observations', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Security Driven Construction Design', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'CPTED Based Risk Mitigation', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Lighting, Fencing & Access Control Planning', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Vendor & Contractor Vetting', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Project Oversight & Quality Control', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Legal & Liability Coordination (via vetted partners)', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Special Projects Oversight (Investors / Capital Improvements)', residential: true, hoa: true, commercial: true, institutional: true },
    { service: 'Law Enforcement Coordination / Emergency Planning', residential: false, hoa: true, commercial: true, institutional: true },
    { service: 'Facility Mapping & Operational Planning', residential: false, hoa: true, commercial: true, institutional: true },
  ];

  const valueDelivery = [
    {
      icon: 'ri-shield-star-line',
      title: 'Law Enforcement Insight',
      description: 'Risk identification, liability awareness, emergency planning, and accountability mindset.'
    },
    {
      icon: 'ri-hammer-line',
      title: 'Construction & Maintenance Knowledge',
      description: 'Real-world understanding of how buildings age, fail, and require intervention beyond surface-level inspections.'
    },
    {
      icon: 'ri-building-line',
      title: 'Property & Building Management Perspective',
      description: 'Focus on continuity, budgeting, prioritization, and long-term asset protection.'
    },
    {
      icon: 'ri-team-line',
      title: 'Vetted Professional Network',
      description: 'Attorneys, general contractors, engineers, inspectors, and skilled trades coordinated under one trusted point of contact.'
    }
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Our Services
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Comprehensive protection solutions tailored to your needs. From <strong>security services</strong> to <strong>storm preparedness</strong> and <strong>preventative maintenance</strong>, we've got you covered.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-teal-200 group relative"
            >
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-600 transition-colors">
                <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                {service.title}
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {service.description}
              </p>
              <ul className="space-y-3 mb-6">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <i className="ri-check-line text-teal-600 text-lg"></i>
                    <span className="text-sm text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
              {service.link && (
                <Link 
                  to={service.link}
                  className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer"
                >
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              )}
            </div>
          ))}
        </div>

        {/* Service Matrix Table */}
        <div className="bg-white rounded-2xl shadow-xl p-8 overflow-hidden mb-20">
          <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Service Coverage by Property Type
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-teal-600 text-white">
                  <th className="px-6 py-4 text-left text-sm font-bold whitespace-nowrap">Service Category</th>
                  <th className="px-6 py-4 text-center text-sm font-bold whitespace-nowrap">Residential</th>
                  <th className="px-6 py-4 text-center text-sm font-bold whitespace-nowrap">HOA / Multifamily</th>
                  <th className="px-6 py-4 text-center text-sm font-bold whitespace-nowrap">Commercial</th>
                  <th className="px-6 py-4 text-center text-sm font-bold whitespace-nowrap">Institutional<br />(Schools / Healthcare / Gov)</th>
                </tr>
              </thead>
              <tbody>
                {serviceMatrix.map((row, index) => (
                  <tr 
                    key={index} 
                    className={`border-b border-gray-200 hover:bg-teal-50 transition-colors ${
                      index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                    }`}
                  >
                    <td className="px-6 py-4 text-sm text-gray-800 font-medium">{row.service}</td>
                    <td className="px-6 py-4 text-center">
                      {row.residential ? (
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl"></i>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {row.hoa ? (
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl"></i>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {row.commercial ? (
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl"></i>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {row.institutional ? (
                        <i className="ri-checkbox-circle-fill text-teal-600 text-xl"></i>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* How SafePoint Delivers Value */}
        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-12 text-white">
          <h3 className="text-3xl md:text-4xl font-bold mb-4 text-center">
            How SafePoint Delivers Value Across All Services
          </h3>
          <p className="text-teal-100 text-center mb-12 max-w-3xl mx-auto">
            Our unique combination of expertise ensures comprehensive property protection
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {valueDelivery.map((item, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-8 hover:bg-white/20 transition-all duration-300 border border-white/20"
              >
                <div className="w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h4 className="text-xl font-bold mb-4">{item.title}</h4>
                <p className="text-teal-100 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}